import { defModule, Task, Update, type TaskMessage, type UpdateResult } from '@st/redux'
import {
  BasicUser,
  DynamicEmailTemplate,
  FolderTag,
  MessageTemplateSubjectVariant,
  Organization,
  OrganizationMembership,
  OrganizationRole,
  STOrganizationState
} from '@st/sdk'
import { groupBy } from '@st/util/array'
import { Effect, pipe } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { Analytics, SdkClient, Snackbar, type BrowserResourceRef } from '~/effects'
import { observeOrganizationRoute, runSdkRequest } from '~/features/module-runtime'
import {
  createFolderTag,
  deleteFolderTag,
  editFolderTag,
  inviteMemberByEmail,
  removeOrganizationMember,
  resendTeamInvite,
  saveMessageTemplates,
  saveOrganization,
  uploadOrganizationLogo,
  type EditOrganizationAttributes,
  type OrganizationWriteOutcome
} from './st-organization-commands'

export type { EditOrganizationAttributes } from './st-organization-commands'

type OrganizationIdleState = {
  status: 'idle'
  organizationSlug: string | null
  currentUserId: string | null
}

type OrganizationLoadingState = {
  status: 'loadingOrganization'
  organizationSlug: string
  currentUserId: string
  organizationId: string
}

type OrganizationLoadedState = {
  status: 'loaded'
  organizationSlug: string
  currentUserId: string
  organizationId: string
  organizationState: STOrganizationState
  refresh: { status: 'idle' } | { status: 'loading' }
  uploadedLogoFilepath: string | null
}

type OrganizationUnauthorizedState = {
  status: 'unauthorized'
  organizationSlug: string
  currentUserId: string
  organizationId: string
}

type OrganizationFailedState =
  | {
      status: 'failed'
      stage: 'resolveOrganization'
      organizationSlug: string
      currentUserId: string
    }
  | {
      status: 'failed'
      stage: 'loadOrganization'
      organizationSlug: string
      currentUserId: string
      organizationId: string
    }

export type STOrganizationModuleState =
  | OrganizationIdleState
  | OrganizationLoadingState
  | OrganizationLoadedState
  | OrganizationUnauthorizedState
  | OrganizationFailedState

const observeActiveOrganization = observeOrganizationRoute({ organizationSlug: P.string })

type OrganizationStateLoaded = {
  type: 'organizationStateLoaded'
  organizationState: STOrganizationState
}

type OrganizationStateUnauthorized = {
  type: 'organizationStateUnauthorized'
}

type OrganizationStateLoadFailed = {
  type: 'organizationStateLoadFailed'
}

const loadOrganizationState = Task.define((_currentUserId: string, organizationId: string) =>
  loadOrganizationStateEffect(organizationId)
)

type OrganizationSessionMessage =
  | TaskMessage<typeof observeActiveOrganization>
  | TaskMessage<typeof loadOrganizationState>
  | { type: 'retryOrganizationStateRequested' }

type OrganizationIntent =
  | {
      type: 'saveMessageTemplates'
      messageTemplateIds: string[]
      comment: string
      subjectVariant: MessageTemplateSubjectVariant
    }
  | { type: 'createFolderTag'; label: string; color: string }
  | { type: 'editFolderTag'; tagId: string; label: string; color: string }
  | { type: 'deleteFolderTag'; tagId: string }
  | { type: 'addMemberByEmailAndSendInvite'; email: string; role: OrganizationRole }
  | { type: 'resendInvite'; userId: string; email: string }
  | { type: 'removeMember'; userId: string; email: string }
  | { type: 'updateOrganization'; modifications: EditOrganizationAttributes }
  | { type: 'uploadOrganizationLogo'; file: BrowserResourceRef<File> }

type OrganizationWriteCompleted = {
  type: 'organizationWriteCompleted'
  organizationId: string
  outcome: OrganizationWriteOutcome
}

type OrganizationLoadedMessage = OrganizationIntent | OrganizationWriteCompleted

export type STOrganizationMessage = OrganizationSessionMessage | OrganizationLoadedMessage

const organizationIntentType = P.union(
  'saveMessageTemplates',
  'createFolderTag',
  'editFolderTag',
  'deleteFolderTag',
  'addMemberByEmailAndSendInvite',
  'resendInvite',
  'removeMember',
  'updateOrganization',
  'uploadOrganizationLogo'
)

/**
 * Route and auth establish one organization session:
 *
 * idle -> resolvingOrganization -> loadingOrganization -> loaded
 *                              \-> failed / unauthorized
 *
 * Resolution and loading are state-owned Tasks. Their arguments identify the
 * current session; replacing the declaration revokes its output, so Task
 * messages contain domain outcomes rather than request or session IDs.
 * Accepted writes are Commands and therefore finish independently. Each
 * outcome carries the organization that accepted the write, so navigation
 * cannot apply it to another organization's loaded state.
 */
export const stOrganizationModule = defModule<{
  state: STOrganizationModuleState
  message: STOrganizationMessage
}>({
  init: () => {
    return {
      status: 'idle',
      organizationSlug: null,
      currentUserId: null
    }
  },
  tasks: organizationTasks,
  update: pipe(
    updateOrganization,
    Update.ignoreWhen<STOrganizationModuleState, STOrganizationMessage>(ignoreOrganizationMessage)
  )
})

type OrganizationUpdate = UpdateResult<STOrganizationModuleState, STOrganizationMessage>
type LoadedOrganizationUpdate = UpdateResult<OrganizationLoadedState, STOrganizationMessage>

function updateOrganization(
  state: STOrganizationModuleState,
  message: STOrganizationMessage
): OrganizationUpdate {
  return match({ state, message })
    .returnType<OrganizationUpdate>()
    .with({ message: { type: 'organizationRouteChanged' } }, ({ state, message }) =>
      reconcileOrganizationContext(state, message.context)
    )
    .with(
      {
        state: P.union(
          { status: 'loadingOrganization' },
          { status: 'loaded', refresh: { status: 'loading' } }
        ),
        message: { type: 'organizationStateLoaded' }
      },
      ({ state, message }) => ({
        status: 'loaded',
        organizationSlug: state.organizationSlug,
        currentUserId: state.currentUserId,
        organizationId: state.organizationId,
        organizationState: message.organizationState,
        refresh: { status: 'idle' },
        uploadedLogoFilepath: null
      })
    )
    .with(
      {
        state: P.union(
          { status: 'loadingOrganization' },
          { status: 'loaded', refresh: { status: 'loading' } }
        ),
        message: { type: 'organizationStateUnauthorized' }
      },
      ({ state }) => ({
        status: 'unauthorized',
        organizationSlug: state.organizationSlug,
        currentUserId: state.currentUserId,
        organizationId: state.organizationId
      })
    )
    .with(
      {
        state: { status: 'loadingOrganization' },
        message: { type: 'organizationStateLoadFailed' }
      },
      ({ state }) => ({
        status: 'failed',
        stage: 'loadOrganization',
        organizationSlug: state.organizationSlug,
        currentUserId: state.currentUserId,
        organizationId: state.organizationId
      })
    )
    .with(
      {
        state: { status: 'loaded', refresh: { status: 'loading' } },
        message: { type: 'organizationStateLoadFailed' }
      },
      ({ state }) => ({ ...state, refresh: { status: 'idle' } })
    )
    .with(
      {
        state: { status: 'failed', stage: 'loadOrganization' },
        message: { type: 'retryOrganizationStateRequested' }
      },
      ({ state }) => ({ ...state, status: 'loadingOrganization' })
    )
    .with(
      {
        state: { status: 'loaded' },
        message: { type: 'organizationWriteCompleted' }
      },
      ({ state, message }) => applyOrganizationWriteOutcome(state, message.outcome)
    )
    .with(
      {
        state: { status: 'loaded' },
        message: { type: organizationIntentType }
      },
      ({ state, message }) => [state, organizationWrite(state, message)]
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function organizationWrite(state: OrganizationLoadedState, message: OrganizationIntent) {
  switch (message.type) {
    case 'saveMessageTemplates':
      return ownOrganizationWrite(
        state,
        saveMessageTemplates({
          organizationId: state.organizationId,
          messageTemplateIds: message.messageTemplateIds,
          comment: message.comment,
          subjectVariant: message.subjectVariant
        })
      )
    case 'createFolderTag':
      return ownOrganizationWrite(
        state,
        createFolderTag({
          organizationId: state.organizationId,
          label: message.label,
          color: message.color
        })
      )
    case 'editFolderTag':
      return ownOrganizationWrite(
        state,
        editFolderTag({
          organizationId: state.organizationId,
          tagId: message.tagId,
          label: message.label,
          color: message.color
        })
      )
    case 'deleteFolderTag':
      return ownOrganizationWrite(
        state,
        deleteFolderTag({ organizationId: state.organizationId, tagId: message.tagId })
      )
    case 'addMemberByEmailAndSendInvite':
      return ownOrganizationWrite(
        state,
        inviteMemberByEmail({
          organizationId: state.organizationId,
          email: message.email,
          role: message.role
        })
      )
    case 'resendInvite':
      return ownOrganizationWrite(
        state,
        resendTeamInvite({
          organizationId: state.organizationId,
          userId: message.userId,
          email: message.email
        })
      )
    case 'removeMember':
      return ownOrganizationWrite(
        state,
        removeOrganizationMember({
          organizationId: state.organizationId,
          userId: message.userId,
          email: message.email
        })
      )
    case 'updateOrganization':
      return ownOrganizationWrite(
        state,
        saveOrganization({
          organizationId: state.organizationId,
          modifications: message.modifications
        })
      )
    case 'uploadOrganizationLogo':
      return ownOrganizationWrite(
        state,
        uploadOrganizationLogo({
          organizationId: state.organizationId,
          file: message.file
        })
      )
  }
}

function ownOrganizationWrite<Error, Requirements>(
  state: OrganizationLoadedState,
  write: Effect.Effect<OrganizationWriteOutcome, Error, Requirements>
) {
  return Effect.map(
    write,
    (outcome): OrganizationWriteCompleted => ({
      type: 'organizationWriteCompleted',
      organizationId: state.organizationId,
      outcome
    })
  )
}

function applyOrganizationWriteOutcome(
  state: OrganizationLoadedState,
  outcome: OrganizationWriteOutcome
): LoadedOrganizationUpdate {
  switch (outcome.type) {
    case 'organizationWriteFailed':
      return withOrganizationSnackbar(state, 'Something went wrong. Please try again.', {
        variant: 'error'
      })
    case 'folderTagCreated':
      if (state.refresh.status == 'loading') return state
      return { ...state, refresh: { status: 'loading' } }
    case 'messageTemplatesUpdated': {
      const ids = new Set(outcome.messageTemplateIds)
      return {
        ...state,
        organizationState: {
          ...state.organizationState,
          messageTemplates: state.organizationState.messageTemplates.map((template) => {
            if (!ids.has(template.id)) return template
            return {
              ...template,
              comment: outcome.comment,
              subjectVariant: outcome.subjectVariant
            }
          })
        }
      }
    }
    case 'organizationUpdated':
      return withOrganizationSnackbar(
        {
          ...state,
          uploadedLogoFilepath: null,
          organizationState: {
            ...state.organizationState,
            organization: {
              ...state.organizationState.organization,
              ...changedOrganizationAttributes(outcome.modifications)
            }
          }
        },
        'Settings saved'
      )
    case 'teamMemberAdded':
      return withOrganizationSnackbar(
        {
          ...state,
          organizationState: {
            ...state.organizationState,
            memberships: [
              ...state.organizationState.memberships,
              {
                organizationId: state.organizationId,
                userId: outcome.user.id,
                role: outcome.role,
                user: outcome.user,
                organization: state.organizationState.organization
              }
            ]
          }
        },
        `Sent invite to ${outcome.user.email}`
      )
    case 'teamInviteResent':
      return withOrganizationSnackbar(state, `Invite resent to ${outcome.email}`)
    case 'teamMemberRemoved':
      return withOrganizationSnackbar(
        {
          ...state,
          organizationState: {
            ...state.organizationState,
            memberships: state.organizationState.memberships.filter(
              (membership) => membership.userId != outcome.userId
            )
          }
        },
        `Removed ${outcome.email}`
      )
    case 'folderTagEdited':
      return {
        ...state,
        organizationState: {
          ...state.organizationState,
          folderTags: state.organizationState.folderTags.map((tag) => {
            if (tag.id != outcome.tagId) return tag
            return { ...tag, label: outcome.label, color: outcome.color }
          })
        }
      }
    case 'folderTagDeleted':
      return {
        ...state,
        organizationState: {
          ...state.organizationState,
          folderTags: state.organizationState.folderTags.map((tag) => {
            if (tag.id != outcome.tagId) return tag
            return { ...tag, deleted: true }
          })
        }
      }
    case 'organizationLogoUploaded':
      return { ...state, uploadedLogoFilepath: outcome.filepath }
    case 'organizationLogoUploadFailed':
      return withOrganizationSnackbar(state, 'Failed to upload organization logo', {
        variant: 'error'
      })
  }
}

function withOrganizationSnackbar(
  state: OrganizationLoadedState,
  message: string,
  options?: { variant: 'error' }
): LoadedOrganizationUpdate {
  return [
    state,
    Effect.gen(function* () {
      const snackbar = yield* Snackbar
      yield* snackbar.show(message, options)
      return null
    })
  ]
}

function changedOrganizationAttributes(
  modifications: EditOrganizationAttributes
): Partial<Pick<Organization, 'name' | 'email' | 'logo'>> {
  const attributes: Partial<Pick<Organization, 'name' | 'email' | 'logo'>> = {}
  if (modifications.name != null) attributes.name = modifications.name
  if (modifications.email != null) attributes.email = modifications.email
  if (modifications.logo != null) attributes.logo = modifications.logo
  return attributes
}

const isOrganizationLoadedMessage = isMatching({
  type: P.union(organizationIntentType, 'organizationWriteCompleted')
})

function ignoreOrganizationMessage({
  state,
  message
}: Readonly<{ state: STOrganizationModuleState; message: STOrganizationMessage }>): boolean {
  if (message.type == 'retryOrganizationStateRequested') {
    return !(state.status == 'failed' && state.stage == 'loadOrganization')
  }

  if (!isOrganizationLoadedMessage(message)) return false
  if (state.status != 'loaded') return true

  if (message.type == 'organizationWriteCompleted') {
    return message.organizationId != state.organizationId
  }

  return false
}

function organizationTasks(state: STOrganizationModuleState) {
  const observations = [observeActiveOrganization()]
  if (state.status == 'loadingOrganization') {
    return [...observations, loadOrganizationState(state.currentUserId, state.organizationId)]
  }
  if (state.status == 'loaded' && state.refresh.status == 'loading') {
    return [...observations, loadOrganizationState(state.currentUserId, state.organizationId)]
  }
  return observations
}

function reconcileOrganizationContext(
  state: STOrganizationModuleState,
  context: TaskMessage<typeof observeActiveOrganization>['context']
): STOrganizationModuleState {
  if (context.status == 'inactive') {
    return {
      status: 'idle',
      organizationSlug: null,
      currentUserId: null
    }
  }

  if (context.status == 'loading' || !context.currentUserId) {
    return {
      status: 'idle',
      organizationSlug: context.route.organizationSlug,
      currentUserId: context.currentUserId
    }
  }

  if (context.status == 'loadFailed' || context.status == 'unavailable') {
    return {
      status: 'failed',
      stage: 'resolveOrganization',
      organizationSlug: context.route.organizationSlug,
      currentUserId: context.currentUserId
    }
  }

  if (
    state.organizationSlug == context.route.organizationSlug &&
    state.currentUserId == context.currentUserId &&
    'organizationId' in state &&
    state.organizationId == context.organization.id
  ) {
    return state
  }

  return {
    status: 'loadingOrganization',
    organizationSlug: context.route.organizationSlug,
    currentUserId: context.currentUserId,
    organizationId: context.organization.id
  }
}

export function selOrganizationId(state: STOrganizationModuleState): string | undefined {
  if ('organizationId' in state) return state.organizationId
  return undefined
}

export function selOrganizationState(
  state: STOrganizationModuleState
): STOrganizationState | undefined {
  return state.status == 'loaded' ? state.organizationState : undefined
}

export function selOrganizationMemberships(
  state: STOrganizationModuleState
): OrganizationMembership[] {
  return state.status == 'loaded' ? state.organizationState.memberships : []
}

export function selOrganizationUsers(state: STOrganizationModuleState): BasicUser[] {
  return selOrganizationMemberships(state).map((membership) => membership.user)
}

export function selOrganizationFolderTags(state: STOrganizationModuleState): FolderTag[] {
  return state.status == 'loaded' ? state.organizationState.folderTags : []
}

export function selOrganizationActiveFolderTags(state: STOrganizationModuleState): FolderTag[] {
  return selOrganizationFolderTags(state).filter((tag) => !tag.deleted)
}

export function selOrganization(state: STOrganizationModuleState): Organization | null {
  return selOrganizationState(state)?.organization ?? null
}

export function selKarbonConnected(state: STOrganizationModuleState): boolean {
  return selOrganizationState(state)?.connectedApps.karbon != null
}

export function selUploadedOrganizationLogoFilepath(
  state: STOrganizationModuleState
): string | null {
  return state.status == 'loaded' ? state.uploadedLogoFilepath : null
}

export type TemplateGroup = {
  label: string
  type: string
  templates: DynamicEmailTemplate[]
}

/**
 * Returns a list of template groups, grouped by type.
 * For example, send questionnaire, remind client, and document request.
 * In the interface, each group basically corresponds to a tab in the settings page.
 *
 * The reason groups are necessary is that, on the backend,
 * we have distinct templates for every combination of message type and return type.
 * For example there is "folder_invite:1040", "folder_invite:1120", etc.
 *
 * But we do not want to complicate the current interface by introducing a tab for every single permutation.
 *
 * Instead, what you effectively do is:
 * - Click the "Send questionnaire" tab (send questionnaire means folder_invite)
 * - Edit the template for "folder_invite:1040"
 * - Click "Save"
 * - The template for "folder_invite:1040" but so is every other template in the folder invite group
 *
 * @param state
 * @returns
 */
export function selTemplateGroups(state: STOrganizationState): TemplateGroup[] {
  return Object.entries(groupBy(state.messageTemplates, (t) => t.label)).map(
    ([label, templates]) => {
      return {
        label: label,
        // by convention, the first tag is the type of template (ex "folder_invite")
        type: templates[0].messageType,
        templates: templates
      }
    }
  )
}

export function selOrganizationTemplateGroups(state: STOrganizationModuleState): TemplateGroup[] {
  const organizationState = selOrganizationState(state)
  return organizationState ? selTemplateGroups(organizationState) : []
}

function loadOrganizationStateEffect(organizationId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({ type: 'organizations/getOrganizationState', organizationId })
    )
    if (result.ok) {
      const analytics = yield* Analytics
      yield* analytics.track('organization_identified', {
        organization_id: organizationId,
        billing_status: result.response.billingStatus
      })
      return {
        type: 'organizationStateLoaded',
        organizationState: result.response
      } satisfies OrganizationStateLoaded
    }
    if (result.error._tag == 'HttpStatusError' && result.error.response.status == 401) {
      return { type: 'organizationStateUnauthorized' } satisfies OrganizationStateUnauthorized
    }
    return { type: 'organizationStateLoadFailed' } satisfies OrganizationStateLoadFailed
  })
}

function unexpectedMessage(
  state: STOrganizationModuleState,
  message: STOrganizationMessage
): never {
  throw new Error(`Unexpected ${message.type} message in ${state.status} organization state`)
}
